import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Suppliers from "@/pages/Suppliers";
import Products from "@/pages/Products";
import Services from "@/pages/Services";
import Stock from "@/pages/Stock";
import Quotations from "@/pages/Quotations";
import QuotationEditor from "@/pages/QuotationEditor";
import SalesInvoices from "@/pages/SalesInvoices";
import SalesInvoiceEditor from "@/pages/SalesInvoiceEditor";
import PurchaseInvoices from "@/pages/PurchaseInvoices";
import PurchaseInvoiceEditor from "@/pages/PurchaseInvoiceEditor";
import Companies from "@/pages/Companies";
import Users from "@/pages/Users";
import GstReports from "@/pages/GstReports";
import Permissions from "@/pages/Permissions";
import InvoicePrint from "@/pages/InvoicePrint";
import Payments from "@/pages/Payments";
import CashBook from "@/pages/CashBook";
import DailyCashClose from "@/pages/DailyCashClose";
import Cheques from "@/pages/Cheques";
import PendingWorks from "@/pages/PendingWorks";
import DscRegister from "@/pages/DscRegister";
import OutwardInward from "@/pages/OutwardInward";
import CredentialsVault from "@/pages/CredentialsVault";
import PendingPayments from "@/pages/PendingPayments";
import Ledger from "@/pages/Ledger";
import PublicInvoice from "@/pages/PublicInvoice";
import SalesReturns from "@/pages/SalesReturns";
import SalesReturnEditor from "@/pages/SalesReturnEditor";
import Reminders from "@/pages/Reminders";
import Performance from "@/pages/Performance";
import Reports from "@/pages/Reports";
import IntegrationsSettings from "@/pages/IntegrationsSettings";
import Stores from "@/pages/Stores";
import PublicQuotation from "@/pages/PublicQuotation";
import PublicTempInvoice from "@/pages/PublicTempInvoice";
import PublicPendingWorks from "@/pages/PublicPendingWorks";
import DataBackup from "@/pages/DataBackup";
import EmployeeHrProfile from "@/pages/EmployeeHrProfile";
import Hrms from "@/pages/Hrms";
import MyHrms from "@/pages/MyHrms";
import NewEmployee from "@/pages/NewEmployee";
import DscList from "@/pages/DscList";
import PermGuard from "@/components/PermGuard";

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex items-center justify-center min-h-screen text-sm text-muted-foreground">
        Loading Vetrich…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={user.role === "hr" ? "/my-hrms" : "/dashboard"} replace />;
  return children;
}

function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.role === "hr" ? "/my-hrms" : "/dashboard"} replace />;
}

function NotHr({ children }) {
  const { user } = useAuth();
  if (user?.role === "hr") return <Navigate to="/my-hrms" replace />;
  return children;
}

function SalaryRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.role === "admin" ? "/hrms?tab=payroll" : "/my-hrms?tab=payroll"} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/p/invoice/:id" element={<PublicInvoice />} />
            <Route path="/p/quotation/:id" element={<PublicQuotation />} />
            <Route path="/p/temp-invoice/:id" element={<PublicTempInvoice />} />
            <Route path="/p/pending-works/:id" element={<PublicPendingWorks />} />
            <Route
              path="/print/invoice/:id"
              element={<Protected><InvoicePrint /></Protected>}
            />
            <Route
              path="/"
              element={<Protected><AppLayout /></Protected>}
            >
              <Route index element={<HomeRedirect />} />
              <Route path="dashboard" element={<NotHr><Dashboard /></NotHr>} />
              <Route path="customers" element={<PermGuard module="customers"><Customers /></PermGuard>} />
              <Route path="suppliers" element={<PermGuard module="suppliers"><Suppliers /></PermGuard>} />
              <Route path="products" element={<PermGuard module="products"><Products /></PermGuard>} />
              <Route path="services" element={<PermGuard module="services"><Services /></PermGuard>} />
              <Route path="stock" element={<PermGuard module="stock"><Stock /></PermGuard>} />
              <Route path="quotations" element={<PermGuard module="quotations"><Quotations /></PermGuard>} />
              <Route path="quotations/new" element={<PermGuard module="quotations" action="create"><QuotationEditor /></PermGuard>} />
              <Route path="quotations/:id" element={<PermGuard module="quotations"><QuotationEditor /></PermGuard>} />
              <Route path="sales" element={<PermGuard module="sales"><SalesInvoices /></PermGuard>} />
              <Route path="sales/new" element={<PermGuard module="sales" action="create"><SalesInvoiceEditor /></PermGuard>} />
              <Route path="sales/:id" element={<PermGuard module="sales"><SalesInvoiceEditor /></PermGuard>} />
              <Route path="purchases" element={<PermGuard module="purchases"><PurchaseInvoices /></PermGuard>} />
              <Route path="purchases/new" element={<PermGuard module="purchases" action="create"><PurchaseInvoiceEditor /></PermGuard>} />
              <Route path="purchases/:id" element={<PermGuard module="purchases"><PurchaseInvoiceEditor /></PermGuard>} />
              <Route path="returns" element={<PermGuard module="returns"><SalesReturns /></PermGuard>} />
              <Route path="returns/new" element={<PermGuard module="returns" action="create"><SalesReturnEditor /></PermGuard>} />
              <Route path="returns/:id" element={<PermGuard module="returns"><SalesReturnEditor /></PermGuard>} />
              <Route path="reminders" element={<PermGuard module="reminders"><Reminders /></PermGuard>} />
              <Route path="performance" element={<PermGuard module="reports"><Performance /></PermGuard>} />
              <Route path="reports" element={<PermGuard module="reports"><Reports /></PermGuard>} />
              <Route path="gst" element={<PermGuard module="gst"><GstReports /></PermGuard>} />
              <Route path="payments" element={<PermGuard module="payments"><Payments /></PermGuard>} />
              <Route path="cashbook" element={<PermGuard module="cash_book"><CashBook /></PermGuard>} />
              <Route path="daily-close" element={<PermGuard module="daily_close"><DailyCashClose /></PermGuard>} />
              <Route path="cheques" element={<PermGuard module="payments"><Cheques /></PermGuard>} />
              <Route path="pending-payments" element={<PermGuard module="sales"><PendingPayments /></PermGuard>} />
              <Route path="pending-works" element={<NotHr><PendingWorks /></NotHr>} />
              <Route path="dsc" element={<NotHr><DscRegister /></NotHr>} />
              <Route path="outward-inward" element={<NotHr><OutwardInward /></NotHr>} />
              <Route path="credentials" element={<NotHr><CredentialsVault /></NotHr>} />
              <Route path="ledger/:partyType/:partyId" element={<Ledger />} />
              <Route path="companies" element={<Protected roles={["admin"]}><Companies /></Protected>} />
              <Route path="stores" element={<Protected roles={["admin"]}><Stores /></Protected>} />
              <Route path="users" element={<Protected roles={["admin", "manager", "regional_manager", "hr"]}><Users /></Protected>} />
              <Route path="permissions" element={<Protected roles={["admin"]}><Permissions /></Protected>} />
              <Route path="settings/integrations" element={<Protected roles={["admin"]}><IntegrationsSettings /></Protected>} />
              <Route path="backup" element={<Protected roles={["admin"]}><DataBackup /></Protected>} />
              <Route path="salary" element={<Protected roles={["admin", "manager", "regional_manager", "hr"]}><SalaryRedirect /></Protected>} />
              <Route path="hr/employees/:eid" element={<Protected roles={["admin", "regional_manager", "hr"]}><EmployeeHrProfile /></Protected>} />
              <Route path="hrms" element={<Protected roles={["admin"]}><Hrms /></Protected>} />
              <Route path="hrms/employees/new" element={<Protected roles={["admin", "regional_manager", "hr"]}><NewEmployee /></Protected>} />
              <Route path="my-hrms" element={<Protected roles={["employee", "manager", "regional_manager", "hr"]}><MyHrms /></Protected>} />
              <Route path="dsc-list" element={<PermGuard module="dsc"><DscList /></PermGuard>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster
            richColors
            position="top-center"
            offset="40vh"
            expand
            duration={6000}
            toastOptions={{
              className: "vt-toast-big",
              style: {
                width: 560,
                minWidth: 560,
                maxWidth: "92vw",
                fontSize: 15,
                fontWeight: 600,
              },
            }}
          />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
